// Which articles are "relevant" right now.
//
// The tab used to need two things nobody had done: the host calling
// setContext() on every route change, and every article carrying hand-assigned
// context keys. Until both happened it fell through to "5 most recently
// updated" — which is why an embed's Relevant tab showed release notes
// (VALCM-7101 #1).
//
// So it now derives the context from the host page's own URL and matches it
// against the metadata articles ALREADY have: folder, tags, title — plus
// context keys when someone has bothered to set them. A partner page at
// /app/lanes/42/edit surfaces the lane articles with no host code and no
// re-tagging.
//
// Precedence, most trustworthy first:
//   1. Explicit context keys (data-context / SkyCellKB.setContext) — a host
//      that states what the user is looking at is never second-guessed.
//   2. URL-derived matches, scored (below).
//   3. For a page whose URL says nothing (a home page, a bare domain, an
//      opaque hash route): the Getting Started folder, where the KB has one.
//      "Most recently updated" put whatever an editor last touched on the
//      home page — Validaide's showed Impersonation, a feature they do not
//      advertise (2026-09-23).
//   4. The 5 most recently updated, for a KB with no Getting Started folder.

export type RelevanceItem = {
  folder: string;
  tags: string[];
  contextKeys: string[];
  title: string;
  updatedAt: string;
};

/** How many URL-matched articles the tab shows. */
const MAX_RELEVANT = 5;

// Path words that describe the plumbing rather than the subject matter. They
// would otherwise match half the KB (every "How to view…" article for "view").
const STOP_WORDS = new Set([
  "app", "apps", "www", "com", "html", "htm", "php", "index", "home", "main",
  "page", "pages", "view", "views", "list", "detail", "details", "edit", "new",
  "create", "update", "delete", "show", "search", "dashboard", "overview",
  "admin", "user", "users", "account", "settings", "profile", "login", "logout",
  "auth", "callback", "api", "public", "static", "assets", "src", "dist",
  "the", "and", "for", "with", "from", "our", "your",
  // language codes that commonly prefix a route
  "en", "de", "fr", "it", "es", "nl", "pt", "pl", "cs", "sv", "da", "fi", "no",
]);

// Anything with a digit in it identifies a record rather than a subject: 42,
// the pieces a UUID breaks into once the path is split, a content hash. Subject
// words in a route effectively never contain one, so this is a cheap rule that
// costs nothing real and removes the whole class.
const isIdentifier = (s: string): boolean => /\d/.test(s);

/** Crude singular form, so a /lanes route matches a "Lane setup" article. */
const stem = (s: string): string => (s.length > 3 && s.endsWith("s") ? s.slice(0, -1) : s);

/**
 * Subject words in a host URL. Path and hash only — never the query string,
 * which is where hosts put tokens, emails and filters.
 */
export function urlTokens(href: string): string[] {
  let path = href;
  try {
    const u = new URL(href);
    path = `${u.pathname} ${u.hash}`;
  } catch {
    /* not a parseable URL — fall back to matching the raw string */
  }
  const seen = new Set<string>();
  for (const raw of path.toLowerCase().split(/[^a-z0-9]+/)) {
    if (!raw || raw.length < 3) continue;
    if (isIdentifier(raw) || STOP_WORDS.has(raw)) continue;
    seen.add(stem(raw));
  }
  return [...seen];
}

/**
 * How well one article matches the current page, 0 = not at all.
 *
 * Weighted by how deliberate each signal is: a context key was assigned by a
 * human for exactly this purpose, a folder is a curated grouping, tags are
 * looser, and a title match is the weakest — a word can appear in a title by
 * coincidence.
 */
export function scoreArticle(item: RelevanceItem, tokens: string[]): number {
  if (!tokens.length) return 0;
  const hay = (values: string[]): string[] =>
    values.join(" ").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).map(stem);

  const keys = new Set(hay(item.contextKeys));
  const folder = new Set(hay([item.folder]));
  const tags = new Set(hay(item.tags));
  const title = new Set(hay([item.title]));

  let score = 0;
  for (const t of tokens) {
    if (keys.has(t)) score += 5;
    else if (folder.has(t)) score += 3;
    else if (tags.has(t)) score += 2;
    else if (title.has(t)) score += 1;
  }
  return score;
}

/** Whether a folder is, or sits under, a top-level "Getting Started". */
const isGettingStarted = (folder: string): boolean =>
  (folder.split("/").find((s) => s.trim()) ?? "").trim().toLowerCase().replace(/[\s_-]+/g, " ") ===
  "getting started";

/**
 * The Relevant tab's list. `context` is what the host declared (wins outright
 * when it matches anything); `href` is the host page URL to fall back to.
 */
export function relevantArticles<T extends RelevanceItem>(
  items: T[],
  context: string[],
  href: string,
): T[] {
  if (context.length) {
    const wanted = new Set(context);
    const hits = items.filter((a) => a.contextKeys.some((k) => wanted.has(k)));
    if (hits.length) return hits;
  }

  const tokens = urlTokens(href);
  const scored = items
    .map((item) => ({ item, score: scoreArticle(item, tokens) }))
    .filter((s) => s.score > 0)
    // Best match first; among equals, the most recently touched article.
    .sort((a, b) => b.score - a.score || (a.item.updatedAt < b.item.updatedAt ? 1 : -1));
  if (scored.length) return scored.slice(0, MAX_RELEVANT).map((s) => s.item);

  const starters = items.filter((a) => isGettingStarted(a.folder));
  if (starters.length) return starters;

  return [...items]
    .filter((a) => a.updatedAt)
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, MAX_RELEVANT);
}
